import {
    RequestApproveShareAgreements,
    RequestCancelShareAgreements,
    RequestCreateShareAgreement,
    RequestUpdateShareAgreement,
    ShareAgreement,
    ShareAgreementsApi,
} from 'oas/datasharing';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../../use-api-mutation';
import { API_NAMES } from '../../constants';

export const useCreateShareAgreements = (): UseMutationResult<
    ShareAgreement[] | undefined,
    unknown,
    { accountLinkId: string } & RequestCreateShareAgreement
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const { accountLinkId, ...createRequest } = attr;
            const res = await new ShareAgreementsApi(apiConfig, '', axiosInstance).createShareAgreements(
                accountLinkId,
                createRequest,
            );

            if (res.status >= 400) {
                return undefined;
            }

            return res?.data;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'share-agreements'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useCancelShareAgreements = (): UseMutationResult<
    ShareAgreement[] | undefined,
    unknown,
    RequestCancelShareAgreements
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const res = await new ShareAgreementsApi(apiConfig, '', axiosInstance).cancelShareAgreements(
                {
                    share_agreement_ids: attr.share_agreement_ids,
                },
                true,
            );

            if (res.status >= 400) {
                return undefined;
            }

            return res?.data;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'share-agreements'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useUpdateShareAgreement = (): UseMutationResult<
    ShareAgreement,
    unknown,
    { shareAgreementId: string } & RequestUpdateShareAgreement
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const { shareAgreementId, ...updateRequest } = attr;
            return (
                await new ShareAgreementsApi(apiConfig, '', axiosInstance).updateShareAgreement(
                    shareAgreementId,
                    true,
                    updateRequest,
                )
            )?.data;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'share-agreements'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useApproveShareAgreements = (): UseMutationResult<
    ShareAgreement[],
    unknown,
    RequestApproveShareAgreements
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            return (await new ShareAgreementsApi(apiConfig, '', axiosInstance).approveShareAgreements(attr))?.data;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'share-agreements'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};
